
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { CreateRegionValidation } from '@/modules/CitiesModule/validations/CitiesActions.validation'

// types
import { RegionListItemT } from '@/core/types/cities.types'
import { CountryT } from '@/core/types/companies.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { companies } from '@/core/realisations/companies.realisation'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<RegionListItemT>,
            default: null,
        },
    },
    emits: ['create', 'cancel', 'delete', 'update'],
    setup(props, { emit }) {
        const state = reactive({
            name: null as string | null,
            countryId: null as number | null,
        })

        const data_state = reactive({
            countries: new DataState<CountryT>({}),
        })

        const state_form$ = useVuelidate(CreateRegionValidation, state)

        // countries

        const loadCountries = async (page: number, search?: string) => {
            data_state.countries.pagination.current_page = page

            try {
                data_state.countries.is_loading = true

                const response = await companies.getCountries(page, search)

                data_state.countries.list = response.data
                data_state.countries.pagination.total_pages = response.meta.last_page

                data_state.countries.is_failed = false
                data_state.countries.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.countries.failure.catchError(error)

                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_failed = true
                }
            } finally {
                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_loading = false
                }
            }
        }

        const loadMoreCountries = async (page: number) => {
            if (page > data_state.countries.pagination.total_pages) return
            data_state.countries.pagination.current_page = page

            try {
                data_state.countries.is_loading = true

                const response = await companies.getCountries(page)
                data_state.countries.list = data_state.countries.list.concat(response.data)

                data_state.countries.pagination.total_pages = response.meta.last_page

                data_state.countries.is_failed = false
                data_state.countries.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.countries.failure.catchError(error)

                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_failed = true
                }
            } finally {
                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_loading = false
                }
            }
        }

        const searchCountries = async (search_query: string) => {
            loadCountries(1, search_query)
        }

        const unshiftFindedCountry = async (country: CountryT) => {
            await loadCountries(data_state.countries.pagination.current_page)
            data_state.countries.list.unshift(country)
        }

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('create', toRaw(state))
        }

        const emitUpdate = () => {
            if (!props.initialValue || !props.initialValue.id) return

            emit('update', props.initialValue.id, toRaw(state))
        }

        const emitDelete = () => {
            if (!props.initialValue || !props.initialValue.id) return

            emit('delete', props.initialValue.id)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            else if (props.isUpdate) emitUpdate()
            else if (props.isDelete) emitDelete()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const setRegion = () => {
            if (props.initialValue) {
                state.name = props.initialValue.name
                state.countryId = props.initialValue.countryId
            }
        }

        onBeforeMount(() => {
            loadCountries(1)
            setRegion()
        })

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление региона'
            if (props.isUpdate) return 'Редактирование региона'
            if (props.isDelete) return 'Удаление региона'
            return 'modal title'
        })
        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        return {
            state,
            data_state,
            state_form$,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            //
            loadCountries,
            loadMoreCountries,
            searchCountries,
            unshiftFindedCountry,
        }
    },
})
